import { useState } from 'react';
import PropTypes from 'prop-types';
import { createForecastValue } from 'Services/forecasts';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'Components/Molecules/SnackbarProvider/SnackbarProvider';
import { trackError } from 'Utils/errorMonitoring';
import ForecastValueCreateFixedForm from './ForecastValueCreateFixedForm';
import ForecastValueCreateCalculatedForm from './ForecastValueCreateCalculatedForm';
import Radio from 'Components/Atoms/Radio/Radio';
import RadioGroup from 'Components/Atoms/Radio/RadioGroup';
import { useTranslation } from 'react-i18next';

const sharedData = {
    reference_id: null,
    reference: 'fixed_value',
    reference_value: null,
    repeat_for: 12,
    repeat_every: 1,
    variation: null,
    variation_value: null,
    operation_span: null,
    operation: null,
};

const FORM_TYPES = {
    FIXED: 'fixed',
    CALCULATED: 'calculated',
};

const REFERENCE_FIXED = 'fixed_value';

function getFormType(forecastValue) {
    if (!forecastValue?.reference) return FORM_TYPES.FIXED;

    return forecastValue.reference === REFERENCE_FIXED ? FORM_TYPES.FIXED : FORM_TYPES.CALCULATED;
}

// The opposite of what the calculated form does when it builds the variation
function getVariationForm(forecastValue) {
    const { variation, variation_value: value } = forecastValue ?? {};

    if (!variation || value === null || value === undefined) return undefined;

    if (variation === 'sum') {
        return value >= 0
            ? { variationId: 'add_value', variationValue: value }
            : { variationId: 'substract_value', variationValue: Math.abs(value) };
    }

    if (value > 1) return { variationId: 'increase_by', variationValue: round(value * 100 - 100) };
    if (value < 1) return { variationId: 'decrease_by', variationValue: round(100 - value * 100) };

    return { variationId: 'multiply_by', variationValue: value };
}

function round(value) {
    return Math.round(value * 100) / 100;
}

export default function ForecastValueCreate(props) {
    const { forecastId, onClose, onCancel = onClose, cell, periodicity, predictedOn, forecastValue } = props;

    const { t } = useTranslation('forecasts');
    const [processing, setProcessing] = useState(false);
    const [activedForm, setActivedForm] = useState(() => getFormType(forecastValue));

    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const onSubmit = async (data) => {
        setProcessing(true);
        const hasRepetition = data.repeat_for > 1;

        try {
            const forecastValue = {
                ...sharedData,
                period_key: cell.filters.date_from,
                item_id: cell.filters.item_id,
                category_type: cell.filters.category_type,
                ...data,
                ...(!hasRepetition && { repeat_every: 1 }),
            };

            await createForecastValue(forecastId, forecastValue);
            await queryClient.invalidateQueries({ queryKey: ['forecasts', forecastId] });
        } catch (error) {
            trackError(error);
            enqueueSnackbar(t('error-creating-forecast-value-title'), {
                variant: 'error',
                description: t('error-creating-forecast-value-description'),
            });
        } finally {
            onClose();
            setProcessing(false);
        }
    };

    return (
        <>
            <RadioGroup
                className="flex column-gap-6 mb-6"
                name="actived-form"
                value={activedForm}
                onChange={(ev) => setActivedForm(ev.target.value)}
            >
                <Radio value={FORM_TYPES.FIXED}>
                    <span className="text-base">{t('cell.fixed-amout')}</span>
                </Radio>
                <Radio value={FORM_TYPES.CALCULATED}>
                    <span className="text-base">{t('cell.calculated')}</span>
                </Radio>
            </RadioGroup>

            {activedForm === FORM_TYPES.FIXED && (
                <ForecastValueCreateFixedForm
                    onCancel={onCancel}
                    onSubmit={onSubmit}
                    processing={processing}
                    periodicity={periodicity}
                    periodKey={cell?.filters.date_from}
                    categoryType={cell?.filters.category_type}
                    defaultValues={
                        forecastValue?.reference === REFERENCE_FIXED
                            ? { reference_value: forecastValue.reference_value }
                            : undefined
                    }
                />
            )}

            {activedForm === FORM_TYPES.CALCULATED && (
                <ForecastValueCreateCalculatedForm
                    categoryType={cell?.filters.category_type}
                    referenceId={cell?.filters.item_id}
                    onCancel={onCancel}
                    onSubmit={onSubmit}
                    processing={processing}
                    periodicity={periodicity}
                    periodKey={cell?.filters.date_from}
                    predictedOn={predictedOn}
                    defaultValues={
                        forecastValue && forecastValue.reference !== REFERENCE_FIXED
                            ? {
                                  reference: forecastValue.reference,
                                  reference_id: forecastValue.reference_id ?? '',
                                  operation: forecastValue.operation ?? 'pointer',
                              }
                            : undefined
                    }
                    defaultVariation={getVariationForm(forecastValue)}
                    defaultOperationSpan={forecastValue?.operation_span ?? undefined}
                />
            )}
        </>
    );
}

ForecastValueCreate.propTypes = {
    onClose: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    forecastId: PropTypes.string.isRequired,
    cell: PropTypes.object,
    periodicity: PropTypes.string,
    predictedOn: PropTypes.string,
    forecastValue: PropTypes.object,
};
